import { ApiError, formatPhone, type PublicUser } from "@rhema/client";
import {
  Avatar,
  Button,
  IconButton,
  ListRow,
  PageHeader,
  SearchField,
  Sheet,
  TrashIcon,
  UserPlusIcon,
} from "@rhema/ui";
import { type FormEvent, useState } from "react";
import { presenceText, toneFor } from "../data/view";
import { FindByEmail } from "../people/FindByEmail";
import { InviteSheet } from "../people/InviteSheet";
import { matchesPerson } from "../people/PeoplePicker";
import { PhoneField, type PhoneValue, phoneOf } from "../people/PhoneField";
import { TextedInvites } from "../people/TextedInvites";
import { deviceRegion, useContactSync } from "../people/useContactSync";
import { contactsChanged, useContacts } from "../people/useContacts";
import { usePhoneEnabled } from "../session/features";
import { useApi, useClientState } from "../session/session";

/** Adds someone by their phone number, or offers an invitation when they are not on Rhema. */
function AddByNumber({ onAdded }: { onAdded: () => void }) {
  const api = useApi();
  const [value, setValue] = useState<PhoneValue>(() => ({ region: deviceRegion(), number: "" }));
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMissing(null);
    const phone = phoneOf(value);
    if (!phone) {
      setError("Check the number. It does not look like a phone number for the country you picked.");
      return;
    }
    setBusy(true);
    try {
      const person = await api.userByPhone(phone);
      await api.addContact(person.id);
      contactsChanged();
      onAdded();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setMissing(phone);
      else setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
      <PhoneField
        label="Phone number"
        value={value}
        onChange={setValue}
        autoFocus
        disabled={busy}
        error={error}
      />
      <Button type="submit" size="lg" disabled={busy || !value.number.trim()}>
        {busy ? "Looking up the number" : "Add contact"}
      </Button>
      {missing && (
        <div role="status" className="flex items-center gap-3 rounded-field bg-surface-header px-3.5 py-2.5">
          <span className="flex-1 text-sm">{formatPhone(missing)} is not on Rhema.</span>
          <Button size="sm" onClick={() => setInviting(true)}>
            Invite to Rhema
          </Button>
        </div>
      )}
      <InviteSheet open={inviting} onClose={() => setInviting(false)} phone={missing ?? undefined} />
    </form>
  );
}

export function ContactsList({ variant, selectedId }: { variant: "phone" | "desktop"; selectedId?: string }) {
  const api = useApi();
  const state = useClientState();
  const { contacts, error } = useContacts();
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const sync = useContactSync();
  const phone = usePhoneEnabled();
  const q = query.trim();
  const visible = [...(contacts ?? [])]
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .filter((p) => matchesPerson(p, q));

  const remove = (person: PublicUser) => {
    api
      .removeContact(person.id)
      .then(contactsChanged)
      .catch((e: Error) => setFailure(e.message));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Contacts"
        size={variant === "desktop" ? "md" : "lg"}
        actions={
          <IconButton label="Add a contact" variant="outline" onClick={() => setAdding(true)}>
            <UserPlusIcon size={19} />
          </IconButton>
        }
      />
      <SearchField
        label="Search contacts"
        placeholder={phone ? "Name or number" : "Name"}
        value={query}
        onChange={setQuery}
        size={variant === "desktop" ? "sm" : "md"}
        className="mx-5 mb-2"
      />
      {sync.available && (
        <Button
          variant="soft"
          size="sm"
          className="mx-5 mb-2 self-start"
          disabled={sync.syncing}
          onClick={() => void sync.refresh()}
        >
          {sync.syncing ? "Checking your phone's contacts" : "Refresh contacts"}
        </Button>
      )}
      {(error || failure || sync.error) && (
        <p role="alert" className="m-0 mx-5 mb-2 text-sm text-scarlet">
          {failure ?? sync.error ?? error}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <ul aria-label="Contacts" className="m-0 list-none p-0">
          {visible.map((p) => {
            const presence = presenceText(state.presence[p.id]);
            return (
              <li key={p.id}>
                <ListRow
                  href={`/contacts/${p.id}`}
                  selected={p.id === selectedId}
                  className={p.id === selectedId ? "bg-surface-header" : "hover:bg-surface-header/60"}
                  leading={
                    <Avatar
                      name={p.display_name}
                      src={p.avatar_url}
                      tone={toneFor(p.id)}
                      size={46}
                      online={presence === "online"}
                    />
                  }
                  title={p.display_name}
                  subtitle={
                    <span className="truncate">{presence ?? (p.phone ? formatPhone(p.phone) : p.about)}</span>
                  }
                  trailing={
                    <IconButton
                      label={`Remove ${p.display_name} from contacts`}
                      variant="ghost"
                      size={36}
                      onClick={() => remove(p)}
                    >
                      <TrashIcon size={17} />
                    </IconButton>
                  }
                />
              </li>
            );
          })}
          {contacts && visible.length === 0 && (
            <li className="px-5 py-6 text-sm text-ink-secondary">
              {q
                ? "No contacts match."
                : sync.available
                  ? "None of your phone's contacts are on Rhema yet. Add someone by their phone number."
                  : "You have no contacts yet. Add someone by their phone number, or open Rhema on your phone to bring in your contacts."}
            </li>
          )}
        </ul>
        <TextedInvites />
      </div>
      <Sheet
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a contact"
        subtitle={
          phone ? "Enter the phone number they use for Rhema" : "Enter the email address they use for Rhema"
        }
        className="px-5 pb-7"
      >
        {phone ? (
          <AddByNumber onAdded={() => setAdding(false)} />
        ) : (
          <FindByEmail
            label="Email address"
            action="Add contact"
            busyText="Looking up the address"
            autoFocus
            onFound={(person) => {
              setFailure(null);
              api
                .addContact(person.id)
                .then(() => {
                  contactsChanged();
                  setAdding(false);
                })
                .catch((e: Error) => setFailure(e.message));
            }}
          />
        )}
      </Sheet>
    </div>
  );
}
